/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import { EmployeeType } from '../types';

export const EMPLOYEE_CONFIG = {
  [EmployeeType.CEO]: {
    name: 'Chief Executive Officer',
    baseSalary: 300000,
    totalCompRange: [500000, 1000000],
    experience: 15,
    productivity: 1.4,
    department: 'Executive',
    level: 10,
    responsibilities: ['Strategic Vision', 'Board Relations', 'Final Decision Authority', 'Public Representation'],
    requiredSkills: ['Leadership', 'Strategic Planning', 'Financial Acumen', 'Public Speaking'],
    marketDemand: 'Low',
    trainingMultiplier: 1.2,
    moraleImpact: 1.5
  },
  [EmployeeType.CTO]: {
    name: 'Chief Technology Officer',
    baseSalary: 250000,
    totalCompRange: [400000, 800000],
    experience: 12,
    productivity: 1.3,
    department: 'Engineering',
    level: 9,
    responsibilities: ['Technology Strategy', 'Architecture Oversight', 'Team Leadership', 'Innovation'],
    requiredSkills: ['Technical Leadership', 'System Architecture', 'Team Management', 'Emerging Tech'],
    marketDemand: 'High',
    trainingMultiplier: 1.3,
    moraleImpact: 1.3
  },
  [EmployeeType.CFO]: {
    name: 'Chief Financial Officer',
    baseSalary: 240000,
    totalCompRange: [380000, 750000],
    experience: 12,
    productivity: 1.3,
    department: 'Finance',
    level: 9,
    responsibilities: ['Financial Strategy', 'Investor Relations', 'Risk Management', 'Regulatory Compliance'],
    requiredSkills: ['Financial Analysis', 'Strategic Planning', 'Regulatory Knowledge', 'Leadership'],
    marketDemand: 'Medium',
    trainingMultiplier: 1.2,
    moraleImpact: 1.2
  },
  [EmployeeType.CRO]: {
    name: 'Chief Risk Officer',
    baseSalary: 220000,
    totalCompRange: [350000, 650000],
    experience: 10,
    productivity: 1.2,
    department: 'Risk',
    level: 9,
    responsibilities: ['Risk Framework', 'Compliance Oversight', 'Audit Management', 'Risk Appetite'],
    requiredSkills: ['Risk Assessment', 'Regulatory Compliance', 'Analytics', 'Leadership'],
    marketDemand: 'High',
    trainingMultiplier: 1.4,
    moraleImpact: 1.1
  },
  [EmployeeType.CCO]: {
    name: 'Chief Compliance Officer',
    baseSalary: 200000,
    totalCompRange: [320000, 600000],
    experience: 10,
    productivity: 1.2,
    department: 'Compliance',
    level: 9,
    responsibilities: ['Compliance Program', 'Regulatory Relations', 'Policy Development', 'Training'],
    requiredSkills: ['Regulatory Knowledge', 'Program Management', 'Communication', 'Leadership'],
    marketDemand: 'Very High',
    trainingMultiplier: 1.5,
    moraleImpact: 1.0
  },
  [EmployeeType.COO]: {
    name: 'Chief Operating Officer',
    baseSalary: 230000,
    totalCompRange: [360000, 700000],
    experience: 12,
    productivity: 1.3,
    department: 'Operations',
    level: 9,
    responsibilities: ['Operational Excellence', 'Process Optimization', 'Team Leadership', 'Strategic Initiatives'],
    requiredSkills: ['Operations Management', 'Process Improvement', 'Leadership', 'Strategic Planning'],
    marketDemand: 'Medium',
    trainingMultiplier: 1.2,
    moraleImpact: 1.3
  },

  // Engineering Team
  [EmployeeType.SeniorEngineer]: {
    name: 'Senior Software Engineer',
    baseSalary: 160000,
    totalCompRange: [220000, 350000],
    experience: 7,
    productivity: 1.2,
    department: 'Engineering',
    level: 6,
    responsibilities: ['Technical Leadership', 'Code Review', 'Mentoring', 'Architecture'],
    requiredSkills: ['Programming', 'System Design', 'Problem Solving', 'Mentoring'],
    marketDemand: 'Very High',
    trainingMultiplier: 1.3,
    moraleImpact: 1.1
  },
  [EmployeeType.Engineer]: {
    name: 'Software Engineer',
    baseSalary: 120000,
    totalCompRange: [160000, 250000],
    experience: 4,
    productivity: 1.0,
    department: 'Engineering',
    level: 5,
    responsibilities: ['Feature Development', 'Code Quality', 'Testing', 'Collaboration'],
    requiredSkills: ['Programming', 'Problem Solving', 'Teamwork', 'Learning'],
    marketDemand: 'Very High',
    trainingMultiplier: 1.4,
    moraleImpact: 1.0
  },
  [EmployeeType.JuniorEngineer]: {
    name: 'Junior Software Engineer',
    baseSalary: 80000,
    totalCompRange: [100000, 140000],
    experience: 1,
    productivity: 0.8,
    department: 'Engineering',
    level: 3,
    responsibilities: ['Feature Development', 'Bug Fixes', 'Learning', 'Documentation'],
    requiredSkills: ['Programming Basics', 'Learning Ability', 'Teamwork', 'Communication'],
    marketDemand: 'High',
    trainingMultiplier: 1.6,
    moraleImpact: 0.9
  },
  [EmployeeType.DevOpsEngineer]: {
    name: 'DevOps Engineer',
    baseSalary: 140000,
    totalCompRange: [180000, 280000],
    experience: 5,
    productivity: 1.1,
    department: 'Engineering',
    level: 5,
    responsibilities: ['CI/CD Pipeline', 'Infrastructure', 'Monitoring', 'Automation'],
    requiredSkills: ['Cloud Platforms', 'Automation', 'Monitoring', 'Security'],
    marketDemand: 'Very High',
    trainingMultiplier: 1.4,
    moraleImpact: 1.0
  },
  [EmployeeType.SecurityEngineer]: {
    name: 'Security Engineer',
    baseSalary: 150000,
    totalCompRange: [200000, 300000],
    experience: 5,
    productivity: 1.1,
    department: 'Security',
    level: 6,
    responsibilities: ['Security Architecture', 'Vulnerability Management', 'Incident Response', 'Compliance'],
    requiredSkills: ['Security Principles', 'Penetration Testing', 'Compliance', 'Incident Response'],
    marketDemand: 'Very High',
    trainingMultiplier: 1.5,
    moraleImpact: 1.1
  },

  // Product & Design
  [EmployeeType.ProductManager]: {
    name: 'Product Manager',
    baseSalary: 140000,
    totalCompRange: [180000, 280000],
    experience: 6,
    productivity: 1.1,
    department: 'Product',
    level: 6,
    responsibilities: ['Product Strategy', 'Roadmap Planning', 'Stakeholder Management', 'User Research'],
    requiredSkills: ['Product Strategy', 'Data Analysis', 'Communication', 'Leadership'],
    marketDemand: 'High',
    trainingMultiplier: 1.3,
    moraleImpact: 1.2
  },
  [EmployeeType.Designer]: {
    name: 'UX/UI Designer',
    baseSalary: 110000,
    totalCompRange: [140000, 200000],
    experience: 4,
    productivity: 1.0,
    department: 'Design',
    level: 5,
    responsibilities: ['User Experience', 'Interface Design', 'User Research', 'Design Systems'],
    requiredSkills: ['Design Tools', 'User Research', 'Visual Design', 'Prototyping'],
    marketDemand: 'High',
    trainingMultiplier: 1.3,
    moraleImpact: 1.1
  },
  [EmployeeType.DataAnalyst]: {
    name: 'Data Analyst',
    baseSalary: 95000,
    totalCompRange: [120000, 160000],
    experience: 3,
    productivity: 1.0,
    department: 'Data',
    level: 4,
    responsibilities: ['Data Analysis', 'Reporting', 'Insights Generation', 'Dashboard Development'],
    requiredSkills: ['SQL', 'Data Visualization', 'Statistics', 'Business Acumen'],
    marketDemand: 'High',
    trainingMultiplier: 1.4,
    moraleImpact: 1.0
  },

  // Compliance & Risk
  [EmployeeType.ComplianceAnalyst]: {
    name: 'Compliance Analyst',
    baseSalary: 85000,
    totalCompRange: [100000, 140000],
    experience: 3,
    productivity: 1.0,
    department: 'Compliance',
    level: 4,
    responsibilities: ['Policy Monitoring', 'Compliance Testing', 'Reporting', 'Training'],
    requiredSkills: ['Regulatory Knowledge', 'Attention to Detail', 'Analysis', 'Communication'],
    marketDemand: 'Very High',
    trainingMultiplier: 1.5,
    moraleImpact: 1.0
  },
  [EmployeeType.AMLAnalyst]: {
    name: 'AML Analyst',
    baseSalary: 90000,
    totalCompRange: [110000, 150000],
    experience: 3,
    productivity: 1.0,
    department: 'Compliance',
    level: 4,
    responsibilities: ['Transaction Monitoring', 'Suspicious Activity Reporting', 'Investigation', 'Regulatory Filing'],
    requiredSkills: ['AML Regulations', 'Investigation', 'Analysis', 'Attention to Detail'],
    marketDemand: 'Very High',
    trainingMultiplier: 1.5,
    moraleImpact: 1.0
  },
  [EmployeeType.RiskAnalyst]: {
    name: 'Risk Analyst',
    baseSalary: 95000,
    totalCompRange: [115000, 160000],
    experience: 4,
    productivity: 1.0,
    department: 'Risk',
    level: 4,
    responsibilities: ['Risk Assessment', 'Modeling', 'Reporting', 'Mitigation Strategies'],
    requiredSkills: ['Risk Assessment', 'Quantitative Analysis', 'Modeling', 'Communication'],
    marketDemand: 'High',
    trainingMultiplier: 1.4,
    moraleImpact: 1.0
  },
  [EmployeeType.InternalAuditor]: {
    name: 'Internal Auditor',
    baseSalary: 90000,
    totalCompRange: [110000, 150000],
    experience: 4,
    productivity: 1.0,
    department: 'Internal Audit',
    level: 5,
    responsibilities: ['Internal Audits', 'Control Testing', 'Reporting', 'Process Improvement'],
    requiredSkills: ['Audit Principles', 'Risk Assessment', 'Analysis', 'Communication'],
    marketDemand: 'High',
    trainingMultiplier: 1.4,
    moraleImpact: 1.0
  },

  // Operations
  [EmployeeType.OperationsManager]: {
    name: 'Operations Manager',
    baseSalary: 100000,
    totalCompRange: [130000, 180000],
    experience: 6,
    productivity: 1.1,
    department: 'Operations',
    level: 6,
    responsibilities: ['Process Management', 'Team Leadership', 'Efficiency Improvement', 'Quality Control'],
    requiredSkills: ['Process Management', 'Leadership', 'Problem Solving', 'Communication'],
    marketDemand: 'Medium',
    trainingMultiplier: 1.2,
    moraleImpact: 1.2
  },
  [EmployeeType.CustomerSupport]: {
    name: 'Customer Support Specialist',
    baseSalary: 45000,
    totalCompRange: [50000, 65000],
    experience: 2,
    productivity: 0.9,
    department: 'Customer Support',
    level: 2,
    responsibilities: ['Customer Assistance', 'Issue Resolution', 'Documentation', 'Feedback Collection'],
    requiredSkills: ['Customer Service', 'Communication', 'Problem Solving', 'Product Knowledge'],
    marketDemand: 'Medium',
    trainingMultiplier: 1.3,
    moraleImpact: 0.9
  },
  [EmployeeType.VendorManager]: {
    name: 'Vendor Manager',
    baseSalary: 95000,
    totalCompRange: [115000, 150000],
    experience: 5,
    productivity: 1.0,
    department: 'Procurement',
    level: 5,
    responsibilities: ['Vendor Relationships', 'Contract Management', 'Performance Monitoring', 'Risk Assessment'],
    requiredSkills: ['Vendor Management', 'Negotiation', 'Risk Assessment', 'Contract Management'],
    marketDemand: 'High',
    trainingMultiplier: 1.4,
    moraleImpact: 1.0
  },

  // Sales & Marketing
  [EmployeeType.SalesRepresentative]: {
    name: 'Sales Representative',
    baseSalary: 70000,
    totalCompRange: [90000, 150000],
    experience: 3,
    productivity: 1.1,
    department: 'Sales',
    level: 3,
    responsibilities: ['Business Development', 'Client Relationships', 'Sales Targets', 'Market Analysis'],
    requiredSkills: ['Sales', 'Communication', 'Negotiation', 'Relationship Building'],
    marketDemand: 'High',
    trainingMultiplier: 1.3,
    moraleImpact: 1.1
  },
  [EmployeeType.MarketingManager]: {
    name: 'Marketing Manager',
    baseSalary: 100000,
    totalCompRange: [130000, 180000],
    experience: 6,
    productivity: 1.1,
    department: 'Marketing',
    level: 6,
    responsibilities: ['Marketing Strategy', 'Campaign Management', 'Brand Management', 'Analytics'],
    requiredSkills: ['Marketing Strategy', 'Analytics', 'Leadership', 'Communication'],
    marketDemand: 'Medium',
    trainingMultiplier: 1.2,
    moraleImpact: 1.2
  },

  // Finance & HR
  [EmployeeType.FinancialAnalyst]: {
    name: 'Financial Analyst',
    baseSalary: 85000,
    totalCompRange: [100000, 140000],
    experience: 3,
    productivity: 1.0,
    department: 'Finance',
    level: 4,
    responsibilities: ['Financial Analysis', 'Forecasting', 'Reporting', 'Budget Management'],
    requiredSkills: ['Financial Analysis', 'Modeling', 'Attention to Detail', 'Communication'],
    marketDemand: 'Medium',
    trainingMultiplier: 1.3,
    moraleImpact: 1.0
  },
  [EmployeeType.HRManager]: {
    name: 'HR Manager',
    baseSalary: 95000,
    totalCompRange: [110000, 150000],
    experience: 6,
    productivity: 1.0,
    department: 'Human Resources',
    level: 6,
    responsibilities: ['Employee Relations', 'Recruitment', 'Performance Management', 'Policy Development'],
    requiredSkills: ['HR Management', 'Communication', 'Problem Solving', 'Legal Knowledge'],
    marketDemand: 'Medium',
    trainingMultiplier: 1.2,
    moraleImpact: 1.2
  },
  [EmployeeType.Recruiter]: {
    name: 'Technical Recruiter',
    baseSalary: 70000,
    totalCompRange: [85000, 120000],
    experience: 3,
    productivity: 1.0,
    department: 'Human Resources',
    level: 3,
    responsibilities: ['Candidate Sourcing', 'Interviewing', 'Offer Management', 'Relationship Building'],
    requiredSkills: ['Recruiting', 'Communication', 'Assessment', 'Technical Knowledge'],
    marketDemand: 'High',
    trainingMultiplier: 1.3,
    moraleImpact: 1.0
  },

  // Legal
  [EmployeeType.LegalCounsel]: {
    name: 'Legal Counsel',
    baseSalary: 140000,
    totalCompRange: [180000, 280000],
    experience: 6,
    productivity: 1.1,
    department: 'Legal',
    level: 7,
    responsibilities: ['Legal Advice', 'Contract Review', 'Regulatory Compliance', 'Risk Mitigation'],
    requiredSkills: ['Legal Knowledge', 'Contract Law', 'Regulatory Compliance', 'Communication'],
    marketDemand: 'High',
    trainingMultiplier: 1.4,
    moraleImpact: 1.1
  },
  [EmployeeType.RegulatoryAffairs]: {
    name: 'Regulatory Affairs Specialist',
    baseSalary: 95000,
    totalCompRange: [115000, 160000],
    experience: 4,
    productivity: 1.0,
    department: 'Legal',
    level: 5,
    responsibilities: ['Regulatory Monitoring', 'Compliance Assistance', 'Reporting', 'Relationship Management'],
    requiredSkills: ['Regulatory Knowledge', 'Communication', 'Analysis', 'Attention to Detail'],
    marketDemand: 'Very High',
    trainingMultiplier: 1.5,
    moraleImpact: 1.0
  }
};

export const EXTERNAL_AUDITOR_CONFIG = {
  [EmployeeType.ExternalAuditor]: {
    name: 'External Auditor',
    dailyRate: 1200,
    firmTypes: ['Big Four', 'National Firm', 'Specialized Firm'],
    certifications: ['CPA', 'CIA'],
    specialties: ['Financial Audit', 'SOC 2', 'PCI DSS', 'Regulatory Compliance']
  },
  [EmployeeType.SOC2Auditor]: {
    name: 'SOC 2 Auditor',
    dailyRate: 1500,
    firmTypes: ['Big Four', 'Specialized IT Audit'],
    certifications: ['CPA', 'CISA', 'CISSP'],
    specialties: ['SOC 2 Type I', 'SOC 2 Type II', 'SOC 1', 'SOC 3']
  },
  [EmployeeType.PCIAuditor]: {
    name: 'PCI DSS Auditor',
    dailyRate: 1800,
    firmTypes: ['Qualified Security Assessor', 'Specialized Security'],
    certifications: ['QSA', 'CISSP', 'CISA'],
    specialties: ['PCI DSS Level 1', 'PCI DSS Level 2', 'PCI DSS Level 3', 'PCI DSS Level 4']
  },
  [EmployeeType.RegulatoryExaminer]: {
    name: 'Regulatory Examiner',
    dailyRate: 0, // Government employees, no direct cost
    agencyTypes: ['Federal Reserve', 'OCC', 'FDIC', 'CFPB', 'SEC', 'State Regulators'],
    focusAreas: ['Safety & Soundness', 'Consumer Protection', 'BSA/AML', 'Information Security']
  }
};

export const DEPARTMENT_BUDGET_ALLOCATION = {
  'Engineering': 0.35,      // 35% of budget
  'Product': 0.08,          // 8% of budget
  'Sales & Marketing': 0.12, // 12% of budget
  'Operations': 0.10,       // 10% of budget
  'Compliance & Risk': 0.15, // 15% of budget
  'Finance': 0.05,          // 5% of budget
  'HR': 0.03,               // 3% of budget
  'Legal': 0.02,            // 2% of budget
  'Executive': 0.10         // 10% of budget
};

export const ORGANIZATIONAL_BENCHMARKS = {
  // Ratios for different company sizes
  startup: { // 0-50 employees
    engineeringRatio: 0.60,
    productRatio: 0.08,
    salesRatio: 0.08,
    operationsRatio: 0.08,
    complianceRatio: 0.06,
    supportRatio: 0.10
  },
  growth: { // 51-200 employees
    engineeringRatio: 0.45,
    productRatio: 0.10,
    salesRatio: 0.12,
    operationsRatio: 0.10,
    complianceRatio: 0.10,
    supportRatio: 0.13
  },
  scale: { // 201+ employees
    engineeringRatio: 0.35,
    productRatio: 0.12,
    salesRatio: 0.15,
    operationsRatio: 0.12,
    complianceRatio: 0.12,
    supportRatio: 0.14
  }
};

export const VENDOR_CATEGORIES = {
  'Software': {
    riskMultiplier: 0.8,
    typicalContractValue: [50000, 500000],
    onboardingTime: 4, // weeks
    reviewFrequency: 'quarterly'
  },
  'Infrastructure': {
    riskMultiplier: 1.2,
    typicalContractValue: [100000, 2000000],
    onboardingTime: 8,
    reviewFrequency: 'monthly'
  },
  'Consulting': {
    riskMultiplier: 0.6,
    typicalContractValue: [75000, 300000],
    onboardingTime: 2,
    reviewFrequency: 'project-based'
  },
  'Payment': {
    riskMultiplier: 1.5,
    typicalContractValue: [100000, 1000000],
    onboardingTime: 12,
    reviewFrequency: 'monthly'
  },
  'Data': {
    riskMultiplier: 1.8,
    typicalContractValue: [25000, 200000],
    onboardingTime: 10,
    reviewFrequency: 'monthly'
  },
  'Security': {
    riskMultiplier: 0.9,
    typicalContractValue: [75000, 500000],
    onboardingTime: 6,
    reviewFrequency: 'quarterly'
  }
};

export const AUDIT_REQUIREMENTS = {
  'SOC2': {
    frequency: 'annually',
    duration: 4, // weeks
    costRange: [25000, 75000],
    preparationTime: 12, // weeks
    scope: ['Security', 'Availability', 'Processing', 'Confidentiality', 'Privacy']
  },
  'PCI': {
    frequency: 'annually',
    duration: 6, // weeks
    costRange: [30000, 100000],
    preparationTime: 16, // weeks
    scope: ['Cardholder Data', 'Network Security', 'Vulnerability Management', 'Access Control']
  },
  'Financial': {
    frequency: 'annually',
    duration: 8, // weeks
    costRange: [100000, 500000],
    preparationTime: 20, // weeks
    scope: ['Financial Controls', 'Revenue Recognition', 'Asset Management', 'Compliance']
  },
  'Regulatory': {
    frequency: 'variable',
    duration: 6, // weeks
    costRange: [50000, 200000],
    preparationTime: 8, // weeks
    scope: ['Consumer Protection', 'BSA/AML', 'Fair Lending', 'Information Security']
  }
};